/**
 * Шторка сравнения: «действующий генплан ↔ проект изменений».
 *
 * Почему две карты, а не одна. MapLibre не умеет обрезать отдельный слой по
 * области экрана: выражения стиля видят атрибуты объекта и зум, но не знают,
 * в каком месте окна объект окажется. Поэтому правая половина — это вторая
 * карта, наложенная поверх первой и обрезанная через CSS clip-path, а обе
 * камеры жёстко синхронизированы. Так делает и mapbox-gl-compare; способ
 * проверенный, ценой служат повторные запросы тайлов, которые кэш браузера
 * почти полностью гасит.
 */

import { MapLibreMap } from "maplibre-gl";
import { el } from "~/ui/dom";

export interface CompareOptions {
  /** Контейнер, в котором лежит основная карта. */
  container: HTMLElement;
  left: MapLibreMap;
  right: MapLibreMap;
  /** Стартовое положение шторки, 0..1. */
  position: number;
  onPositionChange: (position: number) => void;
}

export class Compare {
  private position: number;
  private readonly divider: HTMLElement;
  private readonly options: CompareOptions;
  private syncing = false;
  private dragging = false;
  private readonly abort = new AbortController();
  /** Обработчики синхронизации камер — их нужно снять при destroy(). */
  private cameraSync: { map: MapLibreMap; handler: () => void }[] = [];

  constructor(options: CompareOptions) {
    this.options = options;
    this.position = clamp01(options.position);

    this.divider = el(
      "div",
      {
        class: "compare-divider",
        attrs: {
          role: "separator",
          "aria-orientation": "vertical",
          "aria-label": "Положение шторки сравнения",
          "aria-valuemin": "0",
          "aria-valuemax": "100",
          tabindex: "0",
        },
      },
      el("span", { class: "compare-divider__grip", attrs: { "aria-hidden": "true" } }),
    );

    options.container.appendChild(this.divider);
    this.bindCameraSync();
    this.bindDrag();
    this.apply();
  }

  setPosition(position: number): void {
    this.position = clamp01(position);
    this.apply();
  }

  getPosition(): number {
    return this.position;
  }

  destroy(): void {
    this.abort.abort();
    // AbortController снимает только DOM-слушатели. Обработчики MapLibre
    // нужно отписывать отдельно, иначе каждый цикл включения/выключения
    // шторки оставляет на главной карте лишний «move», который навсегда
    // удерживает уничтоженную вторую карту и работает вхолостую при каждом
    // панорамировании.
    for (const { map, handler } of this.cameraSync) {
      map.off("move", handler);
    }
    this.cameraSync = [];
    this.divider.remove();
    this.options.right.getContainer().style.clipPath = "";
  }

  private apply(): void {
    const width = this.options.container.clientWidth;
    const x = Math.round(width * this.position);

    // Правая карта видна только правее шторки.
    this.options.right.getContainer().style.clipPath = `inset(0 0 0 ${x}px)`;
    this.divider.style.left = `${x}px`;
    this.divider.setAttribute("aria-valuenow", String(Math.round(this.position * 100)));
  }

  /**
   * Двусторонняя синхронизация камер.
   *
   * Флаг syncing обязателен: без него jumpTo на второй карте порождает её
   * собственное событие move, то возвращает движение обратно, и карты входят
   * в бесконечный обмен событиями.
   */
  private bindCameraSync(): void {
    const { left, right } = this.options;

    const mirror = (from: MapLibreMap, to: MapLibreMap) => () => {
      if (this.syncing) return;
      this.syncing = true;
      to.jumpTo({
        center: from.getCenter(),
        zoom: from.getZoom(),
        bearing: from.getBearing(),
        pitch: from.getPitch(),
      });
      this.syncing = false;
    };

    const leftHandler = mirror(left, right);
    const rightHandler = mirror(right, left);
    left.on("move", leftHandler);
    right.on("move", rightHandler);
    this.cameraSync = [
      { map: left, handler: leftHandler },
      { map: right, handler: rightHandler },
    ];
  }

  private bindDrag(): void {
    const { signal } = this.abort;
    const container = this.options.container;

    const moveTo = (clientX: number): void => {
      const rect = container.getBoundingClientRect();
      this.position = clamp01((clientX - rect.left) / rect.width);
      this.apply();
      this.options.onPositionChange(this.position);
    };

    this.divider.addEventListener(
      "pointerdown",
      (event) => {
        this.dragging = true;
        this.divider.setPointerCapture(event.pointerId);
        event.preventDefault();
      },
      { signal },
    );

    this.divider.addEventListener(
      "pointermove",
      (event) => {
        if (this.dragging) moveTo(event.clientX);
      },
      { signal },
    );

    const stop = (event: PointerEvent): void => {
      if (!this.dragging) return;
      this.dragging = false;
      this.divider.releasePointerCapture(event.pointerId);
    };
    this.divider.addEventListener("pointerup", stop, { signal });
    this.divider.addEventListener("pointercancel", stop, { signal });

    // Клавиатура: шторкой должно быть можно управлять без мыши.
    this.divider.addEventListener(
      "keydown",
      (event) => {
        const step = event.shiftKey ? 0.1 : 0.02;
        if (event.key === "ArrowLeft") this.position = clamp01(this.position - step);
        else if (event.key === "ArrowRight") this.position = clamp01(this.position + step);
        else if (event.key === "Home") this.position = 0;
        else if (event.key === "End") this.position = 1;
        else return;
        event.preventDefault();
        this.apply();
        this.options.onPositionChange(this.position);
      },
      { signal },
    );

    window.addEventListener(
      "resize",
      () => {
        this.apply();
      },
      { signal },
    );
  }
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
